import { useState } from 'react'
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material'
import { Link, useLocation } from 'react-router-dom'
import siteConfig from '../config/siteConfig'
import { images } from '../config/images'

const Navbar: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const location = useLocation()

  const menuItems = [
    { label: 'Home', path: '/', visible: siteConfig.pages.home },
    { label: 'About Us', path: '/about', visible: siteConfig.pages.about },
    { label: 'Projects & Competitions', path: '/projects', visible: siteConfig.pages.projects },
    // { label: 'Sponsors', path: '/sponsors', visible: siteConfig.pages.sponsors }, // Deactivated
    { label: 'Contact', path: '/contact', visible: siteConfig.pages.contact },
  ].filter(item => item.visible)

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const drawer = (
    <Box sx={{ width: 280 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
        <Box
          component="img"
          src={images.logo.main}
          alt={images.logo.alt}
          sx={{
            height: '35px',
            width: 'auto',
            objectFit: 'contain',
          }}
        />
        <IconButton onClick={handleDrawerToggle} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <List>
        {menuItems.map((item) => (
          <ListItem
            key={item.path}
            component={Link}
            to={item.path}
            onClick={handleDrawerToggle}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
              borderLeft: location.pathname === item.path ? '4px solid white' : 'none',
              backgroundColor: location.pathname === item.path ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
            }}
          >
            <ListItemText 
              primary={item.label} 
              sx={{
                '& .MuiTypography-root': {
                  fontWeight: location.pathname === item.path ? 700 : 400,
                }
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  )

  return (
    <>
      <AppBar position="sticky" elevation={0}>
        <Toolbar sx={{ justifyContent: 'center', py: 1 }}>
          {/* Centered Layout */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            width: '100%',
            maxWidth: '1200px',
          }}>
            {/* Logo */}
            <Box
              component={Link}
              to="/"
              sx={{
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Box
                component="img"
                src={images.logo.main}
                alt={images.logo.alt}
                sx={{
                  height: { xs: '40px', md: '50px' },
                  width: 'auto',
                  objectFit: 'contain',
                }}
              />
            </Box>

            {/* Desktop Navigation */}
            {isMobile ? (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ 
                  color: theme.palette.primary.main,
                  '&:hover': { 
                    backgroundColor: 'rgba(255, 0, 0, 0.1)',
                    transform: 'rotate(90deg)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <MenuIcon />
              </IconButton>
            ) : (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {menuItems.map((item) => (
                  <Button
                    key={item.path}
                    component={Link}
                    to={item.path}
                    sx={{
                      color: location.pathname === item.path 
                        ? theme.palette.primary.main 
                        : theme.palette.text.primary,
                      fontWeight: location.pathname === item.path ? 700 : 500,
                      px: 2,
                      py: 1,
                      borderRadius: 20,
                      position: 'relative',
                      '&:hover': {
                        color: theme.palette.primary.main,
                        backgroundColor: 'rgba(255, 0, 0, 0.1)',
                        transform: 'translateY(-1px)',
                      },
                      '&::after': location.pathname === item.path ? {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '60%',
                        height: '2px',
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: '2px',
                      } : {},
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 280,
            background: 'linear-gradient(135deg, #FF0000 0%, #FF4444 100%)',
            color: 'white',
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  )
}

export default Navbar
